import { AnimatedLogo } from "@/components/animated-logo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useForm } from "@/components/ui/form";
import { Input, InputElement } from "@/components/ui/input";
import type { PlainModalComponentProps } from "@/components/ui/modal";
import { Separator } from "@/components/ui/separator";
import { loginSchema } from "@/libs/auth/schema";
import * as m from "@/libs/i18n/messages";
import { queries } from "@/utils/query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { LockIcon, UserIcon } from "lucide-react";
import toast from "react-hot-toast";

export function LoginFormCard({
  closeModal,
}: Partial<PlainModalComponentProps>) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false });

  const loginMutation = queries.auth.loginWithBasicCredentials.useMutation({
    onSuccess() {
      toast.success("登录成功，正在跳转到登录前页面...");
      navigate({ to: search.callbackURL ?? "/" });
    },
  });

  const form = useForm(loginSchema, {
    defaultValues: {
      identifier: "",
      password: "",
      rememberMe: false,
    },
    onSubmit: async ({ value }) => {
      await loginMutation.mutateAsync(value);
      if (closeModal) {
        closeModal();
      }
    },
  });

  return (
    <Card className="w-full px-8 py-16 mx-auto xl:max-w-lg 2xl:max-w-xl sm:px-16">
      <CardHeader className="p-0">
        <AnimatedLogo className="block w-auto h-24 mx-auto mb-8 sm:mb-16" />
        <CardTitle className="text-3xl font-normal text-center">
          {m.north_level_shrike_tap()}
        </CardTitle>
      </CardHeader>
      <Separator className="my-4 sm:my-6" />
      <CardContent className="p-0">
        <form.Root className="space-y-4">
          <form.Field
            name="identifier"
            render={(field) => (
              <field.Container
                label={m.still_slimy_pelican_belong()}
                disableController
              >
                <div className="relative">
                  <InputElement>
                    <UserIcon />
                  </InputElement>
                  <field.Controller>
                    <Input placeholder={m.still_slimy_pelican_belong()} />
                  </field.Controller>
                </div>
              </field.Container>
            )}
          />

          <form.Field
            name="password"
            render={(field) => (
              <field.Container
                label={m.fresh_civil_platypus_talk()}
                disableController
              >
                <div className="relative">
                  <InputElement>
                    <LockIcon />
                  </InputElement>
                  <field.Controller>
                    <Input
                      type="password"
                      placeholder={m.fresh_civil_platypus_talk()}
                    />
                  </field.Controller>
                </div>
              </field.Container>
            )}
          />

          <form.Field
            name="rememberMe"
            render={(field) => (
              <field.Container>
                <div className="flex items-center space-x-2">
                  <field.Controller>
                    <Checkbox
                      id={field.name}
                      checked={field.state.value}
                      onCheckedChange={(value) =>
                        field.handleChange(value === true)
                      }
                    />
                  </field.Controller>
                  <label
                    htmlFor={field.name}
                    className="text-sm text-gray-fg-secondary peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {m.bright_true_zebra_arrive()}
                  </label>
                </div>
              </field.Container>
            )}
          />

          <div className="flex items-center justify-between">
            <a
              className="text-sm text-right text-accent-fg-primary"
              href="/auth/reset-password"
            >
              {m.white_each_okapi_ripple()}
            </a>
          </div>

          <div className="pt-4">
            <form.Submit className="w-full">
              {m.tidy_quick_ocelot_nail()}
            </form.Submit>

            <div className="flex pt-2">
              <span className="text-sm text-gray-fg-secondary">
                {m.clear_that_donkey_bask()}
              </span>
              <a className="text-sm text-accent-fg-primary" href="/auth/signup">
                {m.home_broad_snake_thrive()}
              </a>
            </div>
          </div>

          <p className="text-xs text-gray-solid-hover">
            {m.lofty_petty_larva_earn()}
          </p>
        </form.Root>
      </CardContent>
    </Card>
  );
}
